import type { GovernancePolicyFormData } from '@experiences/interfaces';
import { PolicyStatus } from '@experiences/interfaces';
import {
    GovernanceEvent,
    portalTelemetry,
} from '@experiences/telemetry';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import React, {
    useCallback,
    useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { mapPolicyStatusToPolicyMode } from '../../../governanceTelemetryUtils';

interface RadioCard {
    id: string;
    value: string;
    title: string;
    description: string;
    icon: React.ReactNode;
}

const useGovernancePolicyCreateEnforcementStepViewModel = () => {
    const { formatMessage: translate } = useIntl();
    const {
        setValue,
        watch,
    } = useFormContext<GovernancePolicyFormData>();

    const policyStatus = watch('status') ?? PolicyStatus.Simulated;

    const handlePolicyStatusChange = useCallback((mode: string) => {
        const next = mode as PolicyStatus;
        if (next !== policyStatus) {
            portalTelemetry.trackEvent({
                name: GovernanceEvent.PolicyModeSelected,
                properties: {
                    SelectedMode: mapPolicyStatusToPolicyMode(next),
                    PreviousMode: mapPolicyStatusToPolicyMode(policyStatus),
                },
            });
        }
        setValue('status', next, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [ setValue, policyStatus ]);

    const statusModeCards: RadioCard[] = useMemo(() => [
        {
            id: 'simulation',
            title: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_SIMULATION_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_SIMULATION_DESCRIPTION' }),
            icon: <PlayCircleOutlineOutlinedIcon />,
            value: PolicyStatus.Simulated,
        },
        {
            id: 'enabled',
            title: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_ENABLED_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_ENABLED_DESCRIPTION' }),
            icon: <CheckCircleOutlineOutlinedIcon />,
            value: PolicyStatus.Active,
        },
        {
            id: 'disabled',
            title: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_DISABLED_TITLE' }),
            description: translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_DISABLED_DESCRIPTION' }),
            icon: <VisibilityOffOutlinedIcon />,
            value: PolicyStatus.Disabled,
        },
    ], [ translate ]);

    return {
        policyStatus,
        statusModeCards,
        handlePolicyStatusChange,
    };
};

export default useGovernancePolicyCreateEnforcementStepViewModel;
